/**
 * AppointmentsChartTab — Appointments inner tab of the patient Chart shell.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/appts_tab.dart`.
 *
 * Read-only two-section table — Future appointments / Past appointments,
 * the design's AppointmentsChartTab columns (export 21-10
 * chart-appointments.html: Scheduled · Provider · Reason · Office · Room ·
 * Appt; the PA keeps its Created line as a last column; Billing has no
 * feed here) — NOT the filterable 9-column table on the top-level
 * Appointments tab (that's the patient-EDIT surface,
 * `patient_appointments_tab.dart`). The chart copy is deliberately
 * simpler: one page (pageSize 10, `includePrevious: true`), split
 * client-side, no filters / sort / paging / row actions (Flutter's row
 * actions are commented out in `appts_tab.dart`).
 *
 * Split rule (Flutter `appts_tab.dart:97-107`): an appointment is
 * FUTURE when its date is today-or-later AND its status is not
 * Fulfilled / Cancelled / NoShow; everything else (past dates, or any
 * terminal status regardless of date) is PAST.
 *
 * FHIR: aggregate over `Appointment R4` — the timeline is a
 * presentation contract, not a separate resource.
 */

import { ChartEmpty } from './chart-empty';
import { ChartSectionHeader } from './chart-section-header';
import { useInChartPage, useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { EmptyState } from '@scripsteam/scrips-signal-ds';
import { usePatientAppointmentsForChart } from '@/patients/services/use-patient-appointments-for-chart';
import { AppointmentStatusBadge } from '@/patients/components/appointments/appointment-status-badge';
import type { AppointmentForPatient } from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';

export interface AppointmentsChartTabProps {
  patientId: string | null | undefined;
}

/** Terminal statuses that drop an appointment to PAST regardless of date.
 *  Verbatim Flutter `appointmentStatusFulfilled/Cancelled/NoShow`
 *  (status `code`, confirmed against the live wire). */
const TERMINAL_STATUS = new Set(['Fulfilled', 'Cancelled', 'NoShow']);

export function AppointmentsChartTab({ patientId }: AppointmentsChartTabProps) {
  const { data, isLoading, isError, refetch } = usePatientAppointmentsForChart(
    patientId,
    !!patientId,
  );
  // The index's count: the visits the read returned (it is one page: the count is what is known).
  const visits = (data?.appointments ?? []).filter((a) => a.isBlock !== true);
  useReportChartCount(isLoading ? null : visits.length);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState icon="info" title="Save the patient first to see their appointments." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState icon="warning" title="Couldn't load appointments." ctaLabel="Retry" onCtaClick={() => void refetch()} />
      </div>
    );
  }

  const all = visits;
  const future: AppointmentForPatient[] = [];
  const past: AppointmentForPatient[] = [];
  for (const appt of all) {
    (isFuture(appt) ? future : past).push(appt);
  }

  return (
    <div className="p-6 space-y-8">
      <Section title="Future appointments" appts={future} accent="future" />
      <Section title="Past appointments" appts={past} accent="past" />
    </div>
  );
}

function Section({
  title,
  appts,
  accent,
}: {
  title: string;
  appts: AppointmentForPatient[];
  accent: 'future' | 'past';
}) {
  // On the one-page chart the sub-heading is the chart's own (body strong, "· count" in meta), as Active problems.
  const inChartPage = useInChartPage();
  const cap = useRowCap(appts);
  return (
    <section>
      {inChartPage ? (
        <ChartSectionHeader title={title} count={appts.length} showAdd={false} />
      ) : (
        <h3 className="chart-sub-head">
          {title} <span className="chart-sub-count">· {appts.length}</span>
        </h3>
      )}
      {appts.length > 0 ? (
        <div className="chart-plain-table" data-slot="chart-list">
          <table className="w-full">
            <thead>
              <tr>
                <th>Scheduled</th>
                <th>Provider</th>
                <th>Reason</th>
                <th>Office</th>
                <th>Room</th>
                <th>Appt</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {cap.visible.map((appt) => (
                <ApptRow key={appt.id} appt={appt} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ChartEmpty
          title={
            accent === 'future'
              ? 'No future appointments scheduled'
              : 'No past appointments yet'
          }
        />
      )}
      <ChartShowAll cap={cap} label={title.toLowerCase()} />
    </section>
  );
}


function ApptRow({ appt }: { appt: AppointmentForPatient }) {
  const provider = getProvider(appt);
  const providerName = provider?.name?.trim() || 'N/A';
  const speciality = appt.specialty?.code?.displayName?.trim();
  const rfv = appt.reasonCode?.display?.trim() || 'N/A';
  const office = isOffice(appt);

  return (
    <tr data-slot="chart-list-row">
      <td data-chart-cell="date">{formatDateTime(appt.start)}</td>
      <td data-chart-cell="name">
        {providerName}
        {speciality ? <div className="chart-cell-sub">{speciality}</div> : null}
      </td>
      <td data-chart-cell="text">{rfv}</td>
      <td data-chart-cell="text">{office ? appt.practice?.name?.trim() || 'N/A' : appt.location?.name?.trim() || '—'}</td>
      <td data-chart-cell="short">{office ? appt.room?.name?.trim() || 'N/A' : '—'}</td>
      <td data-chart-cell="short">
        <AppointmentStatusBadge status={appt.status?.display ?? null} />
      </td>
      <td data-chart-cell="text">
        {formatDate(appt.created)}
        <div className="chart-cell-sub">{appt.createdPerson || '-'}</div>
      </td>
    </tr>
  );
}

/**
 * FUTURE when the appointment date is today-or-later AND the status is not
 * a terminal one (Fulfilled / Cancelled / NoShow). Date-only comparison,
 * mirroring Flutter's `dateOnly(start).isBefore(dateOnly(now))` guard.
 */
function isFuture(appt: AppointmentForPatient): boolean {
  if (TERMINAL_STATUS.has(appt.status?.code ?? '')) return false;
  if (!appt.start) return false;
  const start = new Date(appt.start);
  if (Number.isNaN(start.getTime())) return false;
  const startDay = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const now = practiceNow();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return startDay.getTime() >= today.getTime();
}

function getProvider(appt: AppointmentForPatient) {
  return appt.participant?.find(
    (p) => p.referenceType?.toLowerCase() === 'provider',
  )?.provider;
}

/** Flutter `appts_tab.dart:171-187`: an Office visit names its practice and room; Virtual / Home name the location. */
function isOffice(appt: AppointmentForPatient): boolean {
  return (appt.location?.name ?? '').toLowerCase() === 'office';
}

/** "Jun 02, 2026, 8:00a" — Flutter `scDateFormat`, `scTimeFormatSingleLetter`. */
function formatDateTime(iso: string | null | undefined): string {
  const date = formatDate(iso);
  const time = formatTimeSingleLetter(iso);
  if (date === '—') return '—';
  return time ? `${date}, ${time}` : date;
}

/** "Jun 02, 2026" — Flutter `scDateFormat`. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

/** "8:00a" / "9:30p" — single-letter am/pm (Flutter `scTimeFormatSingleLetter`). */
function formatTimeSingleLetter(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const hour12 = h % 12 || 12;
  return `${hour12}:${m}${h >= 12 ? 'p' : 'a'}`;
}
